import { useEffect } from "react"
import {
  Block,
  ModalBody,
  ModalFooter,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { useAccount, useChainId, useSwitchChain } from "wagmi"

import { selectWalletAddress, useDappSelector } from "#/redux-state"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import WaitingForActionModalBody from "#/shared/components/modals/WaitingForActionModalBody"
import { ETHEREUM_CHAIN_ID, IS_MEZO_MAINNET } from "#/shared/constants"
import { useBitcoinRecoveryAddress } from "#/shared/hooks"
import {
  useBridgeBitcoin,
  useEstimateBridgeAssetFees,
} from "#/shared/hooks/bridge"
import { InsufficientBitcoinDepositError } from "#/shared/hooks/bridge/useBridgeBitcoin"
import { usePosthogBridgeEvent } from "#/shared/hooks/posthog-events"
import { BridgeAssetFormValue } from "#/shared/types"
import { bigIntToFloat, trimAddress } from "#/shared/utils"
import DepositModalInsufficient from "#/ui-old/Deposit/DepositModal/DepositModalInsufficient"
import DepositModalQR from "#/ui-old/Deposit/DepositModal/DepositModalQR"

import {
  ComponentStepProps,
  StepperModalButton,
  useStepperModalContext,
} from "../StepperModal"

import BridgeSummary from "./BridgeSummary"
import FailedToCreateDepositError from "./FailedToCreateDepositError"

export default function BridgeBTC({ onClickContinue }: ComponentStepProps) {
  const {
    values: { shouldUseAnotherWallet, amount },
  } = useStepperModalContext<BridgeAssetFormValue>()

  const { address, connector } = useAccount()
  const bitcoinRecoveryAddress = useBitcoinRecoveryAddress()

  const chainId = useChainId()

  const {
    switchChain,
    error: switchChainError,
    reset: resetSwitchChain,
  } = useSwitchChain()

  const {
    // Generate deposit.
    depositAddress,
    revealData,
    initializeBitcoinDeposit,
    isInitializingDeposit,
    initializeBitcoinDepositError,
    resetInitializeBitcoinDeposit,
    isBitcoinDepositCreated,

    // Wait for deposit transaction.
    waitForBitcoinDepositTxError,
    retryWaitForBitcoinDepositTx,
    isBitcoinTxConfirmed,

    // Create deposit.
    createDeposit,
    resetCreateDeposit,
    isDepositCreatedSuccessfully,
    createDepositError,

    // Created deposit confirmation.
    confirmDepositCreatedError,
    retryConfirmCreatedDeposit,
    isCreatedDepositConfirmed,

    // General loading indicator.
    isPending,
  } = useBridgeBitcoin(shouldUseAnotherWallet)

  useEffect(() => {
    if (chainId !== ETHEREUM_CHAIN_ID) {
      // We need to switch to Ethereum chain to initialize the tBTC SDK.
      switchChain({ connector, chainId: ETHEREUM_CHAIN_ID })
    }
  }, [switchChain, chainId, connector])

  useEffect(() => {
    if (
      !address ||
      !amount ||
      !bitcoinRecoveryAddress ||
      chainId !== ETHEREUM_CHAIN_ID ||
      switchChainError
    )
      return

    const initialize = async () => {
      await initializeBitcoinDeposit({
        recipient: address,
        amount,
        bitcoinRecoveryAddress,
      })
    }

    if (
      shouldUseAnotherWallet &&
      !isBitcoinDepositCreated &&
      !isInitializingDeposit &&
      !initializeBitcoinDepositError
    ) {
      initialize()
    }
  }, [
    initializeBitcoinDeposit,
    shouldUseAnotherWallet,
    address,
    amount,
    chainId,
    bitcoinRecoveryAddress,
    isBitcoinDepositCreated,
    isInitializingDeposit,
    initializeBitcoinDepositError,
    switchChainError,
  ])

  const { emitAddFundsCompletedEvent } = usePosthogBridgeEvent()
  const walletAddress = useDappSelector(selectWalletAddress)

  const { estimatedTransactionFee, serviceFee } = useEstimateBridgeAssetFees(
    "BTC",
    amount,
  )

  useEffect(() => {
    if (
      isBitcoinTxConfirmed &&
      isDepositCreatedSuccessfully &&
      isCreatedDepositConfirmed
    ) {
      emitAddFundsCompletedEvent(
        "BTC",
        bigIntToFloat(amount ?? 0n, 8),
        walletAddress ?? "",
        "native",
        bigIntToFloat(estimatedTransactionFee, 8),
        bigIntToFloat(serviceFee, 8),
      )
      onClickContinue()
    }
  }, [
    isBitcoinTxConfirmed,
    onClickContinue,
    amount,
    serviceFee,
    walletAddress,
    estimatedTransactionFee,
    emitAddFundsCompletedEvent,
    isDepositCreatedSuccessfully,
    isCreatedDepositConfirmed,
  ])

  const [, theme] = useStyletron()

  if (switchChainError) {
    return (
      <FailedActionModalBody
        title="Invalid network"
        description={`Switch chain to ${
          IS_MEZO_MAINNET ? "Ethereum" : "Sepolia"
        }.`}
        onClickNextStep={() => {
          resetSwitchChain()
          switchChain({ connector, chainId: ETHEREUM_CHAIN_ID })
        }}
      />
    )
  }

  if (waitForBitcoinDepositTxError instanceof InsufficientBitcoinDepositError)
    return <DepositModalInsufficient />

  if (waitForBitcoinDepositTxError) {
    return (
      <FailedActionModalBody
        title="Cannot find Bitcoin deposit transaction"
        description="Please try again."
        onClickNextStep={retryWaitForBitcoinDepositTx}
      />
    )
  }

  if (initializeBitcoinDepositError)
    return (
      <FailedActionModalBody
        title="Cannot generate Bitcoin deposit address"
        description="Please try again."
        onClickNextStep={resetInitializeBitcoinDeposit}
      />
    )

  if (createDepositError)
    return (
      <FailedToCreateDepositError
        title="Failed to create deposit"
        description="Something went wrong."
        depositReceipt={JSON.stringify(revealData)}
        onTryAgain={() => {
          resetCreateDeposit()
          createDeposit()
        }}
      />
    )

  if (confirmDepositCreatedError) {
    return (
      <FailedToCreateDepositError
        title="Failed to confirm deposit creation"
        description="We can't confirm your deposit was created successfully."
        depositReceipt={JSON.stringify(revealData)}
        onTryAgain={retryConfirmCreatedDeposit}
      />
    )
  }

  if (isPending && !shouldUseAnotherWallet)
    return <WaitingForActionModalBody title="Waiting for the transaction..." />

  return (
    <>
      <ModalBody>
        <BridgeSummary />
        {shouldUseAnotherWallet && (
          <Block
            marginTop={theme.sizing.scale900}
            marginBottom={theme.sizing.scale500}
          >
            <DepositModalQR
              depositAmount={bigIntToFloat(amount ?? 0n)}
              walletAddress={address ?? null}
              bitcoinAddress={depositAddress || null}
              description={
                <>
                  <br />
                  <br />
                  Your Deposit will be linked to the address you are logged in
                  with {trimAddress(address ?? "")}.
                </>
              }
            />
          </Block>
        )}
      </ModalBody>
      {!shouldUseAnotherWallet && (
        <ModalFooter>
          <StepperModalButton
            isLoading={isPending}
            onClick={() => {
              initializeBitcoinDeposit({
                amount,
                bitcoinRecoveryAddress,
                recipient: address,
              })
            }}
          >
            Confirm
          </StepperModalButton>
        </ModalFooter>
      )}
    </>
  )
}
